import React, { useEffect, useState } from "react";
import { api, money, statusBadge } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Banknote, ChevronRight, Send, CheckCircle2, Download, Wallet, Clock, X, Loader2 } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

const PayDialog = ({ est, onClose, onDone }) => {
  const { t } = useLang();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(est.total || 0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/estimates/${est.id}/status`, { status: "paid", paid_date: new Date(date).toISOString(), paid_amount: parseFloat(amount) || 0 });
      toast.success(t("saved"));
      onDone();
    } catch { toast.error(t("error")); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{t("recordPayment")}</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-sm font-medium text-slate-900">{est.title}</div>
            <div className="text-xs text-slate-400">{est.number} · {money(est.total)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("paidOn")}</label>
              <input data-testid="paid-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("amountReceived")}</label>
              <input data-testid="paid-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
          <button data-testid="paid-confirm" onClick={submit} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}{t("markPaid")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Receivables() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [payDialog, setPayDialog] = useState(null);

  const load = () => api.get("/estimates?view=receivables").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (est, status) => {
    try { await api.post(`/estimates/${est.id}/status`, { status }); toast.success(t("saved")); load(); }
    catch { toast.error(t("error")); }
  };

  const downloadPdf = async (est) => {
    try {
      const blob = await api.getBlob(`/estimates/${est.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${est.number || "estimate"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("error")); }
  };

  const sum = (st) => items.filter((e) => e.status === st).reduce((s, e) => s + (e.total || 0), 0);
  const toReceive = sum("executed");
  const inBilling = sum("billed");
  const received = items.filter((e) => e.status === "paid").reduce((s, e) => s + (e.paid_amount ?? e.total ?? 0), 0);

  const filtered = filter === "all" ? items : items.filter((e) => e.status === filter);

  const cards = [
    { key: "executed", label: t("toReceive"), value: toReceive, icon: Clock, color: "text-violet-600", bg: "bg-violet-50" },
    { key: "billed", label: t("inBilling"), value: inBilling, icon: Send, color: "text-amber-600", bg: "bg-amber-50" },
    { key: "paid", label: t("received"), value: received, icon: Wallet, color: "text-green-600", bg: "bg-green-50" },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("receivables")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("receivablesHint")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <button key={c.key} data-testid={`receivable-card-${c.key}`} onClick={() => setFilter(filter === c.key ? "all" : c.key)}
            className={`text-left bg-white border rounded-xl p-4 shadow-sm transition-colors ${filter === c.key ? "border-slate-900" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{c.label}</span>
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.bg}`}><c.icon className={`w-4 h-4 ${c.color}`} /></span>
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">{money(c.value)}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">{t("number")}</th>
                <th className="text-left px-4 py-3">{t("title")}</th>
                <th className="text-right px-4 py-3">{t("total")}</th>
                <th className="text-left px-4 py-3">{t("status")}</th>
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((est) => {
                const isOpen = expandedId === est.id;
                return (
                  <React.Fragment key={est.id}>
                    <tr data-testid={`receivable-row-${est.id}`} onClick={() => setExpandedId(isOpen ? null : est.id)} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${isOpen ? "bg-slate-50" : ""}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                          {est.number}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{est.title}</div>
                        <div className="text-xs text-slate-400">{est.unit_name}</div>
                        {est.status === "paid" && est.paid_date && (
                          <div className="text-[11px] text-green-600 mt-0.5">{t("paidOn")} {est.paid_date.slice(0, 10)} · {money(est.paid_amount ?? est.total)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{money(est.total)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(est.status)}`}>{t(est.status)}</span></td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end flex-wrap gap-1.5">
                          <button data-testid={`receivable-pdf-${est.id}`} onClick={() => downloadPdf(est)} className="text-xs font-medium border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /></button>
                          {est.status === "executed" && (
                            <button data-testid={`bill-estimate-${est.id}`} onClick={() => setStatus(est, "billed")} className="text-xs font-medium bg-amber-500 text-white rounded-lg px-2.5 py-1.5 hover:bg-amber-600 inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />{t("sendToBilling")}</button>
                          )}
                          {est.status === "billed" && (
                            <button data-testid={`pay-estimate-${est.id}`} onClick={() => setPayDialog(est)} className="text-xs font-medium bg-green-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-green-700 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />{t("markPaid")}</button>
                          )}
                          {est.status === "paid" && (
                            <span className="text-xs font-medium text-green-700 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{t("paid")}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`receivable-expanded-${est.id}`} className="border-b border-slate-200 bg-slate-50/70">
                        <td colSpan={5} className="px-6 py-4">
                          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("lineItems")}</div>
                          <table className="w-full text-sm">
                            <tbody>
                              {(est.line_items || []).map((li, i) => (
                                <tr key={i} className="border-b border-slate-100 last:border-0">
                                  <td className="py-1.5 text-slate-700">{li.description}</td>
                                  <td className="py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${li.type === "material" ? "bg-cyan-50 text-cyan-600" : "bg-slate-100 text-slate-600"}`}>{t(li.type)}</span></td>
                                  <td className="py-1.5 text-right text-slate-500 tabular-nums">{li.quantity} × {money(li.unit_price)}</td>
                                  <td className="py-1.5 text-right font-medium tabular-nums w-24">{money((li.quantity || 0) * (li.unit_price || 0))}</td>
                                </tr>
                              ))}
                              {(est.line_items || []).length === 0 && <tr><td className="py-2 text-slate-400">{t("noData")}</td></tr>}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                  <Banknote className="w-10 h-10 mx-auto mb-2 text-slate-300" />{t("noData")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payDialog && <PayDialog est={payDialog} onClose={() => setPayDialog(null)} onDone={() => { setPayDialog(null); load(); }} />}
    </div>
  );
}
